import { useEffect, useMemo, useState } from "react";

import { Button, Dropdown, Form, FormField, FormGroup, Input, Modal } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import { BTN_ACTS, EDUCATION_LEVEL_OPTIONS, EducationDataType } from "./sequence/EmployeeInfo";

type ModEmployeeEducationProps = {
  setProp: (key: any, value: any) => void;
  birthDate?: string;
  educationLength: number;
  initialData?: any;
  open: boolean;
  pendingEducationLength: number;
  buttonLoadCheck?: string;
  languageUX: "en" | "th";
  educationCategoryOptions: { key: number; text: string; value: number }[];
  onClose: () => void;
  onSave: (data: EducationDataType) => void;
};

const getInitialEducationState = (): EducationDataType => ({
  id: null,
  category: null,
  degreeName: "",
  educationLevel: "",
  faculty: "",
  graduationStatus: "STUDYING",
  graduationYear: null,
  index: 0,
  institution: "",
  major: "",
});

const ModEmployeeEducation = (props: ModEmployeeEducationProps) => {
  const [educationData, setEducationData] = useState<EducationDataType>(() =>
    getInitialEducationState()
  );
  const [showRequiredField, setShowRequiredField] = useState<Record<string, string>>({});

  // Generate graduation year options
  const graduationYearOptions = useMemo(() => {
    const birthYear =
      (props.birthDate ? new Date(props.birthDate).getFullYear() : new Date().getFullYear() - 60) +
      543;
    const currentYear = new Date().getFullYear() + 543;

    return Array.from({ length: currentYear - birthYear + 1 }, (_, index) => {
      const year = birthYear + index;

      return { key: year, text: `${year}`, value: year };
    }).reverse();
  }, [props.birthDate]);

  // Load initial data when modal opens
  useEffect(() => {
    if (props.open) {
      if (props.initialData) {
        // Map ข้อมูลจาก snake_case หรือ camelCase
        setEducationData({
          id: props.initialData.id || null,
          category: props.initialData.category || null,
          degreeName: props.initialData.degreeName || props.initialData.degree_name || "",
          educationLevel:
            props.initialData.educationLevel || props.initialData.education_level || "",
          faculty: props.initialData.faculty || "",
          graduationStatus:
            props.initialData.graduationStatus || props.initialData.status || "STUDYING",
          graduationYear:
            props.initialData.graduationYear || props.initialData.graduation_year || null,
          index: props.initialData.index || 0,
          institution: props.initialData.institution || "",
          major: props.initialData.major || "",
          tempId: props.initialData.tempId,
        });
      } else {
        setEducationData(getInitialEducationState());
      }

      setShowRequiredField({});
    }
  }, [props.initialData, props.open]);

  const handleEducationChange = (field: string) => (e: any, data: any) => {
    const value = data.value;

    setEducationData((prev) => ({ ...prev, [field]: value }));
  };

  const validateEducationData = () => {
    const errors: Record<string, string> = {};

    if (!educationData.graduationStatus) {
      errors.graduationStatus = "กรุณาระบุสถานภาพการศึกษา";
    }

    if (educationData.graduationStatus === "GRADUATED" && !educationData.graduationYear) {
      errors.graduationYear = "กรุณาระบุปีที่สำเร็จการศึกษา";
    }

    if (!educationData.educationLevel) {
      errors.educationLevel = "กรุณาระบุระดับการศึกษา";
    }

    if (!educationData.category) {
      errors.category = "กรุณาระบุหมวดการศึกษา";
    }

    if (!educationData.institution.trim()) {
      errors.institution = "กรุณาระบุชื่อสถานศึกษา";
    }

    if (!educationData.faculty.trim()) {
      errors.faculty = "กรุณาระบุชื่อคณะ";
    }

    if (!educationData.major.trim()) {
      errors.major = "กรุณาระบุชื่อสาขา";
    }

    if (!educationData.degreeName.trim()) {
      errors.degreeName = "กรุณาระบุชื่อปริญญา";
    }

    if (
      educationData.graduationYear &&
      !graduationYearOptions.some((opt) => opt.value === educationData.graduationYear)
    ) {
      errors.graduationYear = "ปีที่สำเร็จการศึกษาไม่ถูกต้อง";
    }

    if (
      educationData.educationLevel &&
      !EDUCATION_LEVEL_OPTIONS.some((opt) => opt.value === educationData.educationLevel)
    ) {
      errors.educationLevel = "ระดับการศึกษาไม่ถูกต้อง";
    }

    if (
      educationData.category &&
      !props.educationCategoryOptions.some((opt) => opt.value === educationData.category)
    ) {
      errors.category = "หมวดการศึกษาไม่ถูกต้อง";
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validateEducationData();

    setShowRequiredField(errors);

    if (Object.keys(errors).length === 0) {
      props.onSave(educationData);
    }
  };

  const handleClose = () => {
    setEducationData(getInitialEducationState());
    setShowRequiredField({});
    props.onClose();
  };

  return (
    <Modal
      style={{ borderRadius: 10, position: "relative" }}
      open={props.open}
      size="large"
      onClose={handleClose}
    >
      <Modal.Header
        style={{
          border: "none",
          borderRadius: 10,
          boxShadow: "none",
          color: "var(--primary-theme-color)",
          fontSize: "20px",
          paddingBottom: "0px",
          paddingTop: "24px",
        }}
      >
        ลำดับที่{" "}
        {educationData.index === 0
          ? props.educationLength + props.pendingEducationLength + 1
          : educationData.index}
      </Modal.Header>

      <Modal.Content style={{ fontWeight: "bold", paddingBottom: "6rem", textAlign: "center" }}>
        <Form>
          <FormGroup>
            <FormField style={{ marginRight: "12px", textAlign: "left" }} width={5} required>
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Graduation status" : "สถานภาพการศึกษา"}
              </label>
              <Button
                style={{
                  backgroundColor:
                    educationData.graduationStatus === "GRADUATED"
                      ? "var(--primary-theme-color)"
                      : "white",
                  border:
                    educationData.graduationStatus === "GRADUATED"
                      ? "1px solid var(--primary-theme-color)"
                      : "1px solid gray",
                  borderRadius: "10px",
                  color: educationData.graduationStatus === "GRADUATED" ? "white" : "gray",
                  marginRight: "24px",
                  width: "140px",
                }}
                size="small"
                onClick={() =>
                  setEducationData((prev) => ({ ...prev, graduationStatus: "GRADUATED" }))
                }
              >
                จบการศึกษา
              </Button>
              <Button
                style={{
                  backgroundColor:
                    educationData.graduationStatus === "STUDYING"
                      ? "var(--primary-theme-color)"
                      : "white",
                  border:
                    educationData.graduationStatus === "STUDYING"
                      ? "1px solid var(--primary-theme-color)"
                      : "1px solid gray",
                  borderRadius: "10px",
                  color: educationData.graduationStatus === "STUDYING" ? "white" : "gray",
                  width: "140px",
                }}
                size="small"
                onClick={() =>
                  setEducationData((prev) => ({ ...prev, graduationStatus: "STUDYING" }))
                }
              >
                กำลังศึกษา
              </Button>
            </FormField>
            <FormField
              className={showRequiredField.graduationYear ? "error" : ""}
              style={{ marginLeft: "20px", position: "relative", textAlign: "left" }}
              required={educationData.graduationStatus === "GRADUATED"}
              width={4}
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Graduation year" : "ปีที่สำเร็จการศึกษา"}
              </label>
              <Dropdown
                style={{ fontWeight: "normal" }}
                placeholder="-- เลือกปีที่สำเร็จการศึกษา --"
                value={educationData.graduationYear ?? undefined}
                clearable
                scrolling
                selection
                options={graduationYearOptions}
                onChange={handleEducationChange("graduationYear")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.graduationYear || ""}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField
              className={showRequiredField.educationLevel ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={4}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Education level" : "ระดับการศึกษา"}
              </label>
              <Dropdown
                style={{ fontWeight: "normal" }}
                placeholder="-- กรุณาระบุระดับการศึกษา --"
                value={educationData.educationLevel}
                selection
                options={EDUCATION_LEVEL_OPTIONS}
                onChange={handleEducationChange("educationLevel")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.educationLevel || ""}
              </div>
            </FormField>
            <FormField
              className={showRequiredField.category ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={4}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Category" : "หมวดการศึกษา"}
              </label>
              <Dropdown
                style={{ fontWeight: "normal" }}
                placeholder="-- กรุณาระบุหมวดการศึกษา --"
                value={educationData.category ?? undefined}
                selection
                options={props.educationCategoryOptions}
                onChange={handleEducationChange("category")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.category || ""}
              </div>
            </FormField>
            <FormField
              className={showRequiredField.institution ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={8}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Institution" : "สถานศึกษา"}
              </label>
              <Input
                name="institution"
                placeholder="-- กรุณาระบุชื่อสถานศึกษา --"
                value={educationData.institution}
                onChange={handleEducationChange("institution")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.institution || ""}
              </div>
            </FormField>
          </FormGroup>
          <FormGroup>
            <FormField
              className={showRequiredField.faculty ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={5}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Faculty" : "คณะ"}
              </label>
              <Input
                name="faculty"
                placeholder="-- กรุณาระบุชื่อคณะ --"
                value={educationData.faculty}
                onChange={handleEducationChange("faculty")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.faculty || ""}
              </div>
            </FormField>
            <FormField
              className={showRequiredField.major ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={5}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Major" : "สาขา"}
              </label>
              <Input
                name="major"
                placeholder="-- กรุณาระบุชื่อสาขา --"
                value={educationData.major}
                onChange={handleEducationChange("major")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.major || ""}
              </div>
            </FormField>
            <FormField
              className={showRequiredField.degreeName ? "error" : ""}
              style={{ position: "relative", textAlign: "left" }}
              width={6}
              required
            >
              <label style={{ margin: 0, marginBottom: "12px", textAlign: "left", width: "170px" }}>
                {props.languageUX === "en" ? "Degree name" : "ชื่อปริญญา"}
              </label>
              <Input
                name="degree_name"
                placeholder="-- กรุณาระบุชื่อปริญญา --"
                value={educationData.degreeName}
                onChange={handleEducationChange("degreeName")}
              />
              <div
                style={{
                  bottom: "-16px",
                  color: "red",
                  fontSize: "0.75rem",
                  position: "absolute",
                  right: "15px",
                }}
              >
                {showRequiredField.degreeName || ""}
              </div>
            </FormField>
          </FormGroup>
        </Form>
      </Modal.Content>
      <Modal.Actions
        style={{ border: "none", boxShadow: "none", gap: "16px", justifyContent: "center" }}
      >
        <Button onClick={handleClose}>ปิดหน้าต่าง</Button>
        <ButtonLoadCheck
          setProp={props.setProp}
          style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
          paramKey={BTN_ACTS.EDUCATION_SAVE}
          title="บันทึกข้อมูล"
          buttonLoadCheck={props.buttonLoadCheck}
          onClick={handleSave}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModEmployeeEducation;
